/** Long-Term lane tab — separate from swing verdict table. */

import React from "react";
import { longTermStatusPill } from "./longTermChecklist.js";

export function LongTermScannerTable({ rows, sectorLabels = {}, T, onRowClick }) {
  const bySector = {};
  for (const { symbol, result } of rows) {
    const sec = sectorLabels[symbol] || "—";
    bySector[sec] = (bySector[sec] || 0) + 1;
  }

  const hdr = {
    fontSize: 10,
    fontWeight: 700,
    color: T.textDim,
    letterSpacing: "0.06em",
    padding: "8px 10px",
    borderBottom: `2px solid ${T.border}`,
    background: T.surface,
    textAlign: "left",
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ padding: "8px 12px", fontSize: 11, color: T.textSub, borderBottom: `1px solid ${T.border}` }}>
        Sector concentration:{" "}
        {Object.entries(bySector)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([s, n]) => `${s} (${n})`)
          .join(" · ") || "—"}
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={hdr}>Ticker</th>
              <th style={hdr}>Status</th>
              <th style={hdr}>Quality</th>
              <th style={hdr}>Discount</th>
              <th style={hdr}>P/E vs 5y</th>
              <th style={hdr}>% off 52w</th>
              <th style={hdr}>Accum</th>
              <th style={hdr}>Tranches</th>
              <th style={hdr}>Sector</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 24, textAlign: "center", color: T.textDim, fontSize: 12 }}>
                  No long-term candidates in this scan. Run a full scan or check fundamentals cache.
                </td>
              </tr>
            )}
            {rows.map(({ symbol, result }) => {
              const lt = result?.longTerm;
              if (!lt || lt.status === "NONE") return null;
              const pill = longTermStatusPill(lt);
              const pe =
                lt.peTtm != null
                  ? `${lt.peTtm.toFixed(1)}${lt.pe5y != null ? ` / ${lt.pe5y.toFixed(1)}` : ""}`
                  : "—";
              const tr = (lt.tranches || [])
                .map((x) => `${x.id}: $${x.price?.toFixed(2)}`)
                .join(" · ");
              return (
                <tr
                  key={symbol}
                  onClick={() => onRowClick?.(symbol, result)}
                  style={{ borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}
                >
                  <td style={{ padding: "8px 10px", fontWeight: 800, color: T.text }}>{symbol}</td>
                  <td style={{ padding: "8px 10px", fontSize: 10, fontWeight: 800, color: pill?.color ?? T.textSub }}>
                    {pill?.label ?? lt.status}
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: 11, color: T.textSub }}>
                    {lt.qualityScore != null ? `${lt.qualityScore}/5` : "ETF"}
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: 10, color: T.textDim }}>
                    {(lt.discountReasons || []).join("; ") || "—"}
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: 10 }}>{pe}</td>
                  <td style={{ padding: "8px 10px", fontSize: 10 }}>
                    {lt.pctOff52wHigh != null ? `${lt.pctOff52wHigh.toFixed(1)}%` : "—"}
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: 10 }}>{lt.accumulationTag ?? "—"}</td>
                  <td style={{ padding: "8px 10px", fontSize: 9, color: T.textSub }}>{tr || "—"}</td>
                  <td style={{ padding: "8px 10px", fontSize: 10 }}>{sectorLabels[symbol] || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MarketContextBanners({ banners, T }) {
  if (!banners?.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
      {banners.map((b) => (
        <div
          key={b.id}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            background: `${T.cyan}12`,
            border: `1px solid ${T.cyan}44`,
            fontSize: 11,
            fontWeight: 700,
            color: T.cyan,
          }}
        >
          {b.text}
        </div>
      ))}
    </div>
  );
}
